import { useState, useEffect } from "react"
import AdminDialog from "./adminAyarlar"
import { useAuth } from "../context/AuthContext"
import markerService from "../services/markerService"
import TimelineMap from "./maps/TimelineMap"
import { linkifyHtml } from "../utils/textUtils"

const EventPopup = ({
	event,
	onClose,
	onEdit,
	timelineData,
	onTimelineUpdate,
	isAuthenticated,
	isCompact = false,
	categories = [],
	onCategoryFilter,
	position = "center",
}) => {
	const [currentMediaIndex, setCurrentMediaIndex] = useState(0)
	const [currentMediaType, setCurrentMediaType] = useState("image")
	const [isReading, setIsReading] = useState(false)
	const [showAdminDialog, setShowAdminDialog] = useState(false)
	const [isFullScreen, setIsFullScreen] = useState(false)
	const [hideMediaInFullScreen, setHideMediaInFullScreen] = useState(false)
	const { user } = useAuth()
	const [eventMarkers, setEventMarkers] = useState([])
	const [loadingMarkers, setLoadingMarkers] = useState(false)

	// İlk yüklemede varsayılan medya tipini ayarla
	useEffect(() => {
		const initializeMediaType = () => {
			// Mevcut medya tipinde içerik yoksa veya geçersizse
			if (
				!hasMediaType(currentMediaType) ||
				!getMediaContent(currentMediaType).length
			) {
				// Sırayla mevcut medya tiplerini kontrol et
				if (hasMediaType("image")) {
					setCurrentMediaType("image")
				} else if (hasMediaType("video")) {
					setCurrentMediaType("video")
				} else if (hasMediaType("map")) {
					setCurrentMediaType("map")
				} else {
					// Hiç medya yoksa varsayılan olarak image'ı seç
					setCurrentMediaType("image")
				}
			}
			// Medya indeksini sıfırla
			setCurrentMediaIndex(0)
		}

		initializeMediaType()
	}, [event, eventMarkers])

	// Fetch markers for this event
	useEffect(() => {
		const fetchEventMarkers = async () => {
			if (event && timelineData?._id) {
				try {
					setLoadingMarkers(true)
					const markers = await markerService.getEventMarkers(
						timelineData._id,
						event._id
					)

					// Enrich markers with event data
					const enrichedMarkers = markers.map((marker) => ({
						...marker,
						storyEvent_id: event,
					}))

					setEventMarkers(enrichedMarkers)
				} catch (error) {
					console.error(
						"Harita işaretleyicileri yüklenirken hata:",
						error
					)
					setEventMarkers([])
				} finally {
					setLoadingMarkers(false)
				}
			}
		}

		fetchEventMarkers()
	}, [event, timelineData])

	// Cleanup effect
	useEffect(() => {
		return () => {
			if (window.speechSynthesis) {
				window.speechSynthesis.cancel()
			}
		}
	}, [])

	// Handle fullscreen changes for map resize
	useEffect(() => {
		if (currentMediaType === "map") {
			// Trigger map resize after fullscreen state changes
			const timer = setTimeout(() => {
				window.dispatchEvent(new Event("mapResize"))
			}, 300) // Give time for the transition to complete

			return () => clearTimeout(timer)
		}
	}, [isFullScreen, currentMediaType])

	// Add/remove body class when media is hidden in fullscreen
	useEffect(() => {
		if (isFullScreen && hideMediaInFullScreen) {
			document.body.classList.add('event-popup-media-hidden')
		} else {
			document.body.classList.remove('event-popup-media-hidden')
		}

		// Cleanup on unmount
		return () => {
			document.body.classList.remove('event-popup-media-hidden')
		}
	}, [isFullScreen, hideMediaInFullScreen])

	// Herhangi bir medya olup olmadığını kontrol eden fonksiyon
	const hasAnyMedia = () => {
		return (
			(Array.isArray(event.images) && event.images.length > 0) ||
			(Array.isArray(event.videos) && event.videos.length > 0) ||
			(Array.isArray(event.mapImages) && event.mapImages.length > 0)
		)
	}

	// Belirli bir medya tipinin var olup olmadığını kontrol eden fonksiyon
	const hasMediaType = (type) => {
		if (type === "harita" || type === "map") {
			return eventMarkers && eventMarkers.length > 0
		}
		const mediaContent = getMediaContent(type)
		return Array.isArray(mediaContent) && mediaContent.length > 0
	}

	// Medya tipine göre içerik listesini döndüren yardımcı fonksiyon
	const getMediaContent = (type) => {
		switch (type) {
			case "image":
				return Array.isArray(event.images) ? event.images : []
			case "video":
				return Array.isArray(event.videos) ? event.videos : []
			case "map":
			case "harita":
				// For interactive map, we check if there are markers
				return eventMarkers && eventMarkers.length > 0
					? ["interactive_map"]
					: []
			default:
				return []
		}
	}

	// Medya tipini değiştiren fonksiyon
	const changeMediaType = (type) => {
		// Eğer tıklanan medya tipi mevcutsa ve şu anki tipten farklıysa değiştir
		if (hasMediaType(type) && type !== currentMediaType) {
			setCurrentMediaType(type)
			setCurrentMediaIndex(0)

			// If switching to map, trigger a resize after layout is complete
			if (type === "map") {
				setTimeout(() => {
					window.dispatchEvent(new Event("mapResize"))
					// Also trigger standard resize for good measure
					window.dispatchEvent(new Event("resize"))
				}, 100)
			}
		}
	}

	function getEmbeddedVideoURL(url) {
		// Handle various YouTube URL formats
		if (url.includes("youtube") || url.includes("youtu.be")) {
			// Extract video ID
			let videoId = ""

			// Handle youtu.be short links
			if (url.includes("youtu.be/")) {
				videoId = url.split("youtu.be/")[1]
				// Remove any parameters
				if (videoId.includes("?")) {
					videoId = videoId.split("?")[0]
				}
			}
			// Handle regular youtube.com links
			else if (url.includes("watch?v=")) {
				videoId = url.split("watch?v=")[1]
				// Remove any additional parameters
				if (videoId.includes("&")) {
					videoId = videoId.split("&")[0]
				}
			}
			// Handle youtube.com/embed links (already in embed format)
			else if (url.includes("/embed/")) {
				// Already in embed format, just ensure no extra parameters
				return url.split("?")[0]
			}

			// Construct proper embed URL
			if (videoId) {
				return `https://www.youtube.com/embed/${videoId}`
			}
		} else if (url.includes("dailymotion")) {
			let tmp = url
				.replace("www.", "geo.")
				.replace("/video/", "/player.html?video=")
			return tmp
		}

		// If nothing matched or we couldn't extract ID, return original URL
		return url
	}

	// Medya gezinme fonksiyonları
	const nextMedia = () => {
		const mediaList = getMediaContent(currentMediaType)
		setCurrentMediaIndex((prev) => (prev + 1) % mediaList.length)
	}

	const prevMedia = () => {
		const mediaList = getMediaContent(currentMediaType)
		setCurrentMediaIndex(
			(prev) => (prev - 1 + mediaList.length) % mediaList.length
		)
	}

	// Sesli okuma fonksiyonu
	const readText = () => {
		if (!window.speechSynthesis) {
			alert("Tarayıcınız sesli okuma özelliğini desteklemiyor.")
			return
		}

		// Eğer zaten okuma yapılıyorsa durdur
		if (isReading) {
			window.speechSynthesis.cancel()
			setIsReading(false)
			return
		}

		// URL'leri metinden temizle
		const cleanText = (text) => {
			// Create temporary element to strip HTML
			const tempDiv = document.createElement("div")
			tempDiv.innerHTML = text
			const plainText = tempDiv.textContent || tempDiv.innerText || ""

			const urlRegex = /(https?:\/\/[^\s]+)/g
			return plainText.replace(urlRegex, "link")
		}

		const cleanTitle = cleanText(event.title)
		const cleanDescription = cleanText(event.description)
		const text = `${cleanTitle}. ${cleanDescription}`

		const utterance = new SpeechSynthesisUtterance(text)
		utterance.lang = "tr-TR"

		// Ses ayarlarını al
		const volumeControl = document.querySelector(
			'input[type="range"][min="0"][max="1"]'
		)
		const rateControl = document.querySelector(
			'input[type="range"][min="0.5"][max="2"]'
		)
		const pitchControl = document.querySelector(
			'input[type="range"][min="0.5"][max="2"]'
		)

		if (volumeControl) utterance.volume = parseFloat(volumeControl.value)
		if (rateControl) utterance.rate = parseFloat(rateControl.value)
		if (pitchControl) utterance.pitch = parseFloat(pitchControl.value)

		// Okuma bittiğinde state'i güncelle
		utterance.onend = () => {
			setIsReading(false)
		}

		setIsReading(true)
		window.speechSynthesis.speak(utterance)
	}



	// Content helper
	const renderDescription = (content) => {
		if (!content) return null

		const processedContent = linkifyHtml(content)

		return <div dangerouslySetInnerHTML={{ __html: processedContent }} />
	}

	// Resim hata işleyicisi ekliyoruz
	const handleImageError = (e) => {
		const originalSrc = e.target.src
		console.error("Resim yükleme hatası. Orijinal URL:", originalSrc)

		try {
			const apiUrl =
				process.env.REACT_APP_API_URL?.trim() || window.location.origin

			// URL'yi analiz et
			const url = new URL(originalSrc)
			const path = url.pathname

			if (path.includes("/uploads/")) {
				// API URL'sini değiştirerek tekrar dene
				const newSrc = `${apiUrl}${path}`
				if (newSrc !== originalSrc) {
					console.log("Alternatif URL deneniyor:", newSrc)
					e.target.src = newSrc
					return
				}
			}

			// Hiçbir şey işe yaramazsa placeholder göster
			e.target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='45%25' font-family='Arial' font-size='16' fill='%23999' text-anchor='middle'%3EResim Yüklenemedi%3C/text%3E%3Ctext x='50%25' y='55%25' font-family='Arial' font-size='12' fill='%23666' text-anchor='middle'%3E${encodeURIComponent(
				path
			)}%3C/text%3E%3C/svg%3E`
		} catch (error) {
			console.error("Hata yönetimi sırasında hata:", error)
		}
	}

	if (!event) return null

	const handleOutsideClick = (e) => {
		if (e.target === e.currentTarget && !isFullScreen) {
			onClose()
		}
	}

	const processMediaUrl = (url) => {
		if (!url) return null

		try {
			// Eğer tam URL ise direkt döndür
			if (url.startsWith("http://") || url.startsWith("https://")) {
				return url
			}

			// API URL'ini al
			const apiUrl =
				process.env.REACT_APP_API_URL?.trim() || window.location.origin

			// URL'deki çift slashları temizle
			const cleanUrl = (baseUrl, path) => {
				return `${baseUrl.replace(/\/+$/, "")}/${path.replace(
					/^\/+/,
					""
				)}`
			}

			// /uploads ile başlayan URL'leri işle
			if (url.includes("/uploads/")) {
				// URL'den gereksiz slashları temizle
				return cleanUrl(apiUrl, url)
			}

			// Diğer göreceli yolları işle
			if (url.startsWith("/")) {
				return cleanUrl(window.location.origin, url)
			}

			// Hiçbir kurala uymuyorsa, /uploads/ ekleyerek dene
			return cleanUrl(apiUrl, `/uploads/${url}`)
		} catch (error) {
			console.error("URL işleme hatası:", error, "URL:", url)
			return url
		}
	}

	const renderMedia = () => {
		const mediaList = getMediaContent(currentMediaType)
		const currentContent = mediaList[currentMediaIndex]

		if (!currentContent) {
			return (
				<div className="flex items-center justify-center h-full bg-gray-50">
					<div className="text-center p-8">
						<svg
							xmlns="http://www.w3.org/2000/svg"
							className="h-10 w-10 mx-auto text-gray-400 mb-2"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
							/>
						</svg>
						<p className="text-gray-500 font-medium">
							Medya bulunamadı
						</p>
					</div>
				</div>
			)
		}

		switch (currentMediaType) {
			case "image":
				return (
					<div
						className={`relative h-full bg-gray-900 flex items-center justify-center ${isFullScreen ? "pt-2 pb-2 pr-2 pl-0" : ""
							}`}
					>
						{/* Close button for fullscreen mode */}
						{isFullScreen && (
							<button
								onClick={() => setHideMediaInFullScreen(true)}
								className="absolute top-2 right-2 sm:top-4 sm:right-4 z-50 bg-black/70 hover:bg-black/90 text-white p-3 sm:p-2 rounded-full transition-all duration-200 shadow-lg"
								title="Resmi Gizle"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									className="h-6 w-6 sm:h-5 sm:w-5"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M6 18L18 6M6 6l12 12"
									/>
								</svg>
							</button>
						)}
						<img
							src={processMediaUrl(currentContent)}
							alt={event.title}
							onError={handleImageError}
							className={`object-contain max-h-full max-w-full ${isFullScreen ? "h-full" : "h-full]"
								}`}
							loading="lazy"
						/>
						{mediaList.length > 1 && (
							<>
								<button
									onClick={prevMedia}
									className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2.5 rounded-full transition-all duration-200"
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="h-4 w-4"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M15 19l-7-7 7-7"
										/>
									</svg>
								</button>
								<button
									onClick={nextMedia}
									className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2.5 rounded-full transition-all duration-200"
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="h-4 w-4"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M9 5l7 7-7 7"
										/>
									</svg>
								</button>
								<div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/60 px-2.5 py-1 rounded-full text-white text-xs font-medium">
									{currentMediaIndex + 1} / {mediaList.length}
								</div>
							</>
						)}
					</div>
				)
			case "video":
				return (
					<div
						className={`relative h-full ${isFullScreen ? "pt-2 pb-2 pr-2 pl-0" : ""
							}`}
					>
						{/* Close button for fullscreen mode */}
						{isFullScreen && (
							<button
								onClick={() => setHideMediaInFullScreen(true)}
								className="absolute top-2 right-2 sm:top-4 sm:right-4 z-50 bg-black/70 hover:bg-black/90 text-white p-3 sm:p-2 rounded-full transition-all duration-200 shadow-lg"
								title="Videoyu Gizle"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									className="h-6 w-6 sm:h-5 sm:w-5"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M6 18L18 6M6 6l12 12"
									/>
								</svg>
							</button>
						)}
						<div className="w-full h-full overflow-hidden">
							<iframe
								src={getEmbeddedVideoURL(currentContent)}
								title="Video Player"
								frameBorder="0"
								className={`w-full ${isFullScreen ? "h-full" : "h-full"
									}`}
								allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
								referrerPolicy="strict-origin-when-cross-origin"
								allowFullScreen
							></iframe>
						</div>
						{mediaList.length > 1 && (
							<>
								<button
									onClick={prevMedia}
									className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2.5 rounded-full transition-all duration-200"
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="h-4 w-4"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M15 19l-7-7 7-7"
										/>
									</svg>
								</button>
								<button
									onClick={nextMedia}
									className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2.5 rounded-full transition-all duration-200"
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="h-4 w-4"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M9 5l7 7-7 7"
										/>
									</svg>
								</button>
								<div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/60 px-2.5 py-1 rounded-full text-white text-xs font-medium">
									{currentMediaIndex + 1} / {mediaList.length}
								</div>
							</>
						)}
					</div>
				)
			case "map":
				return (
					<div
						className={`relative h-full ${isFullScreen ? "pt-2 pb-2 pr-2 pl-0" : ""
							}`}
					>
						{/* Close button for fullscreen mode */}
						{isFullScreen && (
							<button
								onClick={() => setHideMediaInFullScreen(true)}
								className="absolute top-2 right-2 sm:top-4 sm:right-4 z-50 bg-black/70 hover:bg-black/90 text-white p-3 sm:p-2 rounded-full transition-all duration-200 shadow-lg"
								title="Haritayı Gizle"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									className="h-6 w-6 sm:h-5 sm:w-5"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M6 18L18 6M6 6l12 12"
									/>
								</svg>
							</button>
						)}
						{loadingMarkers ? (
							<div className="flex items-center justify-center h-full bg-gray-50">
								<div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
								<p className="ml-4 text-sm text-gray-600">
									Harita yükleniyor...
								</p>
							</div>
						) : (
							<div className="w-full h-full">
								<TimelineMap
									markers={eventMarkers}
									onMapClick={null}
									onMarkerDelete={null}
									onMarkerDragEnd={null}
									onMarkerSizeChange={null}
									onMarkerClick={null}
								/>
							</div>
						)}
					</div>
				)
			default:
				return null
		}
	}

	// Helper function to get category info by ID
	const getCategoryInfo = (categoryId) => {
		if (!categoryId || !categories || categories.length === 0) {
			return null
		}

		// Extract the ID string
		let idStr = categoryId
		if (typeof categoryId === "object") {
			idStr = categoryId._id || categoryId.id || String(categoryId)
		}

		// Find the category
		const category = categories.find((cat) => {
			const catIdStr = String(cat._id)
			return catIdStr === String(idStr) || catIdStr.endsWith(String(idStr).slice(-10))
		})

		return category || null
	}

	// Get the category to display (linked_category takes priority over main category)
	const getDisplayCategoryId = () => {
		return event.linked_category_id || event.category_id
	}

	// Handle category filter button click
	const handleCategoryFilterClick = () => {
		const categoryToFilter = getDisplayCategoryId()
		if (categoryToFilter && onCategoryFilter) {
			onCategoryFilter(categoryToFilter)
			onClose()
		}
	}

	// Check if event has a category (either linked or main)
	const hasCategory = () => {
		const categoryId = getDisplayCategoryId()
		return categoryId && getCategoryInfo(categoryId) !== null
	}

	// Toggle fullscreen function
	const toggleFullScreen = () => {
		setIsFullScreen(!isFullScreen)
		// Reset media visibility when exiting fullscreen
		if (isFullScreen) {
			setHideMediaInFullScreen(false)
		}
	}

	return (
		<div
			className={`fixed z-50 ${
				isFullScreen
					? 'left-0 right-0 bottom-0'
					: position === 'right'
					? 'top-0 right-0 h-full flex items-stretch justify-end'
					: 'flex items-start justify-center isolate top-16 md:top-[112px]'
			}`}
			onClick={handleOutsideClick}
			style={{
				backgroundColor: "transparent",
				pointerEvents: "auto",
				backdropFilter: "none",
				left: (!isFullScreen && position === 'right') ? 'auto' : 0,
				right: 0,
				bottom: 0,
				top: isFullScreen ? '80px' : undefined,
			}}
		>
			<div
				className={`bg-white shadow-2xl flex relative transition-all duration-300 overflow-hidden ${isFullScreen
					? "w-full h-full rounded-none mt-0 flex-col"
					: position === 'right'
					? "w-[400px] h-[85vh] mt-[60px] mr-2 rounded-xl flex-col border border-gray-200"
					: "w-full md:w-[650px] h-full md:h-[300px] rounded-none md:rounded-xl m-0 md:m-4 flex-row"
					}`}
				onClick={(e) => e.stopPropagation()}
				style={{
					pointerEvents: "auto",
					position: isFullScreen ? "absolute" : "relative",
					top: isFullScreen ? 0 : "auto",
					left: isFullScreen ? 0 : "auto",
					right: isFullScreen ? 0 : "auto",
					bottom: isFullScreen ? 0 : "auto",
				}}
			>
				<div
					className={`flex ${hasMediaType(currentMediaType) &&
						!(isFullScreen && hideMediaInFullScreen)
						? isFullScreen ? "flex-row" : position === 'right' ? "flex-col" : "flex-col md:flex-row"
						: ""
						} h-full`}
				>
					{hasMediaType(currentMediaType) &&
						!(isFullScreen && hideMediaInFullScreen) && (
							<div
								className={`relative ${isFullScreen
									? "w-[70%] h-full"
									: position === 'right'
									? "w-full h-[200px]"
									: "w-full md:w-[65%] h-[50%] md:h-full"
									} min-h-0 flex-grow-0 flex-shrink-0 bg-black`}
							>
								{renderMedia()}
							</div>
						)}

					<div
						className={`flex flex-col ${hasMediaType(currentMediaType) &&
							!(isFullScreen && hideMediaInFullScreen)
							? isFullScreen
								? "w-[30%] h-full"
								: position === 'right'
								? "w-full flex-1"
								: "w-full md:w-[35%] h-[50%] md:h-full"
							: "w-full"
							} overflow-hidden`}
					>
						<div className="p-3 md:p-4 flex flex-col h-full">
							{/* Header with date and controls */}
							<div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 sm:mb-3 flex-shrink-0 gap-2">
								<div className="flex items-center bg-gray-100 px-2 py-1 rounded-lg shadow-sm">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="h-3 w-3 sm:h-4 sm:w-4 mr-1 text-gray-600"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
										/>
									</svg>
									<span className="text-gray-700 font-medium text-xs sm:text-sm">
										{event.startDate.toString()}
										{event.endDate &&
											` - ${event.endDate.toString()}`}
									</span>
								</div>

								<div className="flex items-center gap-1">
									<button
										onClick={readText}
										className={`p-1.5 rounded-full transition-all duration-200 ${isReading
											? "bg-blue-100 text-blue-700"
											: "bg-gray-100 text-gray-600 hover:bg-gray-200"
											}`}
										title={
											isReading
												? "Okumayı Durdur"
												: "Sesli Dinle"
										}
									>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											className="h-3 w-3 sm:h-4 sm:w-4"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
											/>
										</svg>
									</button>

									<button
										onClick={toggleFullScreen}
										className={`p-1.5 rounded-full transition-all duration-200 ${isFullScreen
											? "bg-blue-100 text-blue-700"
											: "bg-gray-100 text-gray-600 hover:bg-gray-200"
											}`}
										title={
											isFullScreen
												? "Tam Ekrandan Çık"
												: "Tam Ekran"
										}
									>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											className="h-3 w-3 sm:h-4 sm:w-4"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
										>
											{isFullScreen ? (
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M9 9L4 4m0 0l5 0m-5 0l0 5M9 15l-5 5m0 0l5 0m-5 0l0 -5M15 9l5 -5m0 0l0 5m0 -5l-5 0M15 15l5 5m0 0l-5 0m5 0l0 -5"
												/>
											) : (
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M4 8V4m0 0h4M4 4l5 5m11-5h-4m4 0v4m0-4l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5h-4m4 0v-4m0 4l-5-5"
												/>
											)}
										</svg>
									</button>

									{isAuthenticated &&
										user &&
										timelineData &&
										user._id === timelineData.user_id && (
											<button
												onClick={() => {
													setShowAdminDialog(true)
													if (onEdit) {
														onEdit(event)
													}
												}}
												className="p-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full transition-all duration-200"
												title="Düzenle"
											>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													className="h-3 w-3 sm:h-4 sm:w-4"
													fill="none"
													viewBox="0 0 24 24"
													stroke="currentColor"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
													/>
												</svg>
											</button>
										)}

									<button
										onClick={onClose}
										className="p-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full transition-all duration-200"
										title="Kapat"
									>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											className="h-3 w-3 sm:h-4 sm:w-4"
											fill="none"
											viewBox="0 0 24 24"
											stroke="currentColor"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M6 18L18 6M6 6l12 12"
											/>
										</svg>
									</button>
								</div>
							</div>

							{/* Title */}
							<h2
								className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3 flex-shrink-0 rich-text-content"
								dangerouslySetInnerHTML={{
									__html: event.title,
								}}
							/>

							{/* Show media button when hidden in fullscreen */}
							{isFullScreen &&
								hideMediaInFullScreen &&
								hasMediaType(currentMediaType) && (
									<button
										onClick={() =>
											setHideMediaInFullScreen(false)
										}
										className="mb-3 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-all duration-200 flex-shrink-0 w-fit"
									>
										Medya Göster
									</button>
								)}

							{/* Description - Scrollable area */}
							<div className="flex-grow overflow-y-auto mb-2 sm:mb-3">
								<div className="pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
									<div className="text-gray-700 text-xs sm:text-sm leading-relaxed whitespace-pre-wrap rich-text-content">
										{renderDescription(event.description)}
									</div>
								</div>
							</div>

							{/* Media controls - Fixed at bottom */}
							{(hasMediaType("image") ||
								hasMediaType("video") ||
								hasMediaType("map") ||
								event.category_id ||
								event.linked_category_id) && (
									<div
										className={`${isFullScreen
											? "fixed bottom-2 sm:bottom-4 right-2 sm:right-4 bg-white p-1.5 sm:p-2 rounded-lg shadow-lg"
											: "mt-auto"
											} flex-shrink-0`}
									>
										<div className="grid grid-cols-4 gap-0.5 sm:gap-1">
											<button
												onClick={() =>
													changeMediaType("image")
												}
												className={`flex flex-col items-center justify-center py-1.5 sm:py-2 px-0.5 sm:px-1 rounded-lg transition-all duration-200 ${currentMediaType === "image" &&
													hasMediaType("image")
													? "bg-purple-600 text-white"
													: hasMediaType("image")
														? "bg-gray-100 text-gray-700 hover:bg-gray-200"
														: "bg-gray-100 text-gray-400 cursor-not-allowed opacity-50"
													}`}
												disabled={!hasMediaType("image")}
											>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													className="h-3 w-3 sm:h-4 sm:w-4 mb-0.5 sm:mb-1"
													fill="none"
													viewBox="0 0 24 24"
													stroke="currentColor"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
													/>
												</svg>
												<span className="text-[10px] sm:text-xs">
													Resim{" "}
													{hasMediaType("image") &&
														`(${getMediaContent("image")
															.length
														})`}
												</span>
											</button>

											<button
												onClick={() =>
													changeMediaType("video")
												}
												className={`flex flex-col items-center justify-center py-1.5 sm:py-2 px-0.5 sm:px-1 rounded-lg transition-all duration-200 ${currentMediaType === "video" &&
													hasMediaType("video")
													? "bg-red-600 text-white"
													: hasMediaType("video")
														? "bg-gray-100 text-gray-700 hover:bg-gray-200"
														: "bg-gray-100 text-gray-400 cursor-not-allowed opacity-50"
													}`}
												disabled={!hasMediaType("video")}
											>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													className="h-3 w-3 sm:h-4 sm:w-4 mb-0.5 sm:mb-1"
													fill="none"
													viewBox="0 0 24 24"
													stroke="currentColor"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
													/>
												</svg>
												<span className="text-[10px] sm:text-xs">
													Video{" "}
													{hasMediaType("video") &&
														`(${getMediaContent("video")
															.length
														})`}
												</span>
											</button>

											<button
												onClick={() =>
													changeMediaType("map")
												}
												className={`flex flex-col items-center justify-center py-1.5 sm:py-2 px-0.5 sm:px-1 rounded-lg transition-all duration-200 ${currentMediaType === "map" &&
													hasMediaType("map")
													? "bg-green-600 text-white"
													: hasMediaType("map")
														? "bg-gray-100 text-gray-700 hover:bg-gray-200"
														: "bg-gray-100 text-gray-400 cursor-not-allowed opacity-50"
													}`}
												disabled={
													!hasMediaType("map") ||
													loadingMarkers
												}
											>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													className="h-3 w-3 sm:h-4 sm:w-4 mb-0.5 sm:mb-1"
													fill="none"
													viewBox="0 0 24 24"
													stroke="currentColor"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
													/>
												</svg>
												<span className="text-[10px] sm:text-xs">
													Harita{" "}
													{hasMediaType("map") &&
														`(${eventMarkers.length})`}
												</span>
											</button>

											<button
												onClick={handleCategoryFilterClick}
												className={`flex flex-col items-center justify-center py-1.5 sm:py-2 px-0.5 sm:px-1 rounded-lg transition-all duration-200 ${hasCategory()
													? "bg-gray-100 text-gray-700 hover:bg-gray-200"
													: "bg-gray-100 text-gray-400 cursor-not-allowed opacity-50"
													}`}
												disabled={!hasCategory() || !onCategoryFilter}
												title={hasCategory() ? `${getCategoryInfo(getDisplayCategoryId())?.name || "Kategori"} ile filtrele` : "Kategori yok"}
											>
												{hasCategory() && (
													<span
														className="w-3 h-3 sm:w-4 sm:h-4 rounded-full mb-0.5 sm:mb-1 border border-gray-300"
														style={{
															backgroundColor: getCategoryInfo(getDisplayCategoryId())?.color || "#808080",
														}}
													/>
												)}
												{!hasCategory() && (
													<svg
														xmlns="http://www.w3.org/2000/svg"
														className="h-3 w-3 sm:h-4 sm:w-4 mb-0.5 sm:mb-1"
														fill="none"
														viewBox="0 0 24 24"
														stroke="currentColor"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
														/>
													</svg>
												)}
												<span className="text-[10px] sm:text-xs truncate max-w-full">
													{hasCategory()
														? getCategoryInfo(getDisplayCategoryId())?.name || "Kategori"
														: "Kategori"}
												</span>
											</button>
										</div>
									</div>
								)}
						</div>
					</div>
				</div>
			</div>

			{showAdminDialog && (
				<AdminDialog
					open={showAdminDialog}
					onClose={() => setShowAdminDialog(false)}
					settings={timelineData}
					onSettingsChange={onTimelineUpdate}
					activeTab={1}
					initialEditingStory={event}
				/>
			)}
		</div>
	)
}

export default EventPopup
